import { config } from "./config";
import { tokenManager } from "./auth/tokenManager";
import { getBaseHeaders, handleApiError } from "./auth/utils";

// Helper for authenticated requests
const makeRequest = async (url: string, options: RequestInit = {}) => {
  const response = await tokenManager.makeAuthenticatedRequest(
    `${config.API_URL}${url}`,
    options
  );

  if (!response.ok) {
    await handleApiError(response, "Request");
  }

  return response.json();
};

// Helper for public (non-authenticated) requests
const makePublicRequest = async (url: string, options: RequestInit = {}) => {
  const response = await fetch(`${config.API_URL}${url}`, {
    ...options,
    headers: {
      ...getBaseHeaders(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    await handleApiError(response, "Request");
  }

  return response.json();
};

// ==================== ENUMS ====================

export enum EventType {
  PARTY = "Party",
  BIRTHDAY = "Birthday",
  BABY_SHOWER = "Baby Shower",
  WEDDING = "Wedding",
  ANNIVERSARY = "Anniversary",
  GRADUATION = "Graduation",
  CONFERENCE = "Conference",
  WORKSHOP = "Workshop",
  REUNION = "Reunion",
  ENGAGEMENT = "Engagement",
  BRIDAL_SHOWER = "Bridal Shower",
  HOUSEWARMING = "Housewarming",
  CORPORATE = "Corporate",
  CUSTOM = "Custom",
}

export enum EventStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  CANCELLED = "CANCELLED",
  COMPLETED = "COMPLETED",
}

export enum Visibility {
  PRIVATE = "PRIVATE",
  PUBLIC = "PUBLIC",
}

export enum RSVPStatus {
  PENDING = "PENDING",
  YES = "YES",
  NO = "NO",
  MAYBE = "MAYBE",
}

export enum ParticipantRole {
  ORGANIZER = "ORGANIZER",
  CO_ORGANIZER = "CO_ORGANIZER",
  PARTICIPANT = "PARTICIPANT",
}

export enum TaskStatus {
  OPEN = "OPEN",
  CLAIMED = "CLAIMED",
  COMPLETED = "COMPLETED",
}

// ==================== INTERFACES ====================

export interface LocationCoordinates {
  lat: number;
  lng: number;
}

export interface Event {
  id: number;
  name: string;
  description?: string;
  event_type: string; // Changed from EventType enum to string
  organizer_id: number;
  visibility: Visibility;
  status: EventStatus;
  start_date: string;
  end_date?: string; // Changed to optional
  location_name?: string;
  location_address?: string;
  location_coordinates?: LocationCoordinates;
  is_virtual: boolean;
  virtual_link?: string;
  dress_code?: string;
  max_participants?: number;
  allow_plus_ones: boolean;
  max_plus_ones: number; // Changed to required with default 1
  require_approval: boolean;
  enable_qr_checkin: boolean;
  custom_fields?: Record<string, any>;
  participant_count: number;
  invitation_poster_url?: string;
  created_at: string;
  updated_at: string;
}

// Public participant response (limited info for non-organizers)
export interface ParticipantPublicResponse {
  id: number;
  name: string;
  role: ParticipantRole;
  rsvp_status: RSVPStatus;
  checked_in: boolean;
  table_number?: string;
}

// Full participant response (for organizers)
export interface Participant extends ParticipantPublicResponse {
  user_id?: number;
  email?: string;
  phone?: string;
  rsvp_date?: string;
  plus_ones: number;
  plus_one_names?: string[];
  checkin_time?: string;
  dietary_restrictions?: string[];
  special_requests?: string;
  is_registered: boolean;
  created_at: string;
}

export interface Task {
  id: number;
  title: string;
  description?: string;
  category?: string;
  status: TaskStatus;
  assigned_to?: number;
  assigned_name?: string;
  quantity_needed: number;
  quantity_claimed: number;
  due_date?: string;
  created_at: string;
}

export interface Rule {
  id: number;
  rule_type: string;
  rule_value: string;
  is_mandatory: boolean;
  created_at: string;
}

export interface TimelineItem {
  id: number;
  time: string;
  title: string;
  description?: string;
  location?: string;
  order_index: number;
  created_at: string;
}

export interface SeatingTable {
  id: number;
  table_name: string;
  capacity: number;
  assigned_seats?: Record<string, boolean>;
  notes?: string;
  created_at: string;
}

export interface Media {
  id: number;
  file_url: string;
  file_type: string;
  file_name: string;
  file_size: number;
  uploaded_by: number;
  is_public: boolean;
  media_type: "photo" | "document" | "video";
  created_at: string;
}

export interface FunFact {
  id: number;
  fact_text: string;
  fact_year: number;
  fact_category: string;
  source: string;
}

export interface FunFactCreate {
  fact_text: string;
  fact_year?: number;
  fact_category?: string;
  source?: string;
}

export interface EventDetail extends Event {
  participants: Participant[];
  tasks: Task[];
  rules: Rule[];
  timeline: TimelineItem[];
  seating: SeatingTable[];
  media: Media[];
  fun_facts: FunFact[];
  is_organizer: boolean;
  is_co_organizer: boolean;
  my_participant_id?: number;
  qr_code_data?: string; // Base64 QR image when QR check-in enabled
}

// Paginated response interface
export interface PaginatedResponse<T> {
  events: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface CreateEventData {
  name: string;
  description?: string;
  event_type: string; // Changed from EventType enum to string
  status?: EventStatus;
  visibility: Visibility;
  start_date: string;
  end_date?: string; // Changed to optional
  location_name?: string;
  location_address?: string;
  location_coordinates?: LocationCoordinates;
  is_virtual: boolean;
  virtual_link?: string;
  dress_code?: string;
  max_participants?: number;
  allow_plus_ones: boolean;
  max_plus_ones?: number;
  require_approval: boolean;
  enable_qr_checkin: boolean;
  custom_fields?: Record<string, any>;
  invitation_poster_url?: string;
}

export interface AddParticipantData {
  user_id?: number;
  email?: string;
  name?: string;
  phone?: string;
  role: ParticipantRole;
  is_registered: boolean;
}

export interface RSVPData {
  status: RSVPStatus;
  plus_ones?: number;
  plus_one_names?: string[];
  dietary_restrictions?: string[];
  special_requests?: string;
}

export interface UpdateParticipantRoleData {
  role: ParticipantRole;
}

export interface CreateTaskData {
  title: string;
  description?: string;
  category?: string;
  quantity_needed: number;
  due_date?: string;
}

export interface UpdateTaskData {
  title?: string;
  description?: string;
  category?: string;
  quantity_needed?: number;
  due_date?: string;
  status?: TaskStatus;
}

export interface CreateRuleData {
  rule_type: string;
  rule_value: string;
  is_mandatory: boolean;
}

export interface UpdateRuleData {
  rule_type?: string;
  rule_value?: string;
  is_mandatory?: boolean;
}

export interface CreateTimelineData {
  time: string;
  title: string;
  description?: string;
  location?: string;
  order_index: number;
}

export interface UpdateTimelineData {
  time?: string;
  title?: string;
  description?: string;
  location?: string;
  order_index?: number;
}

export interface CreateSeatingData {
  table_name: string;
  capacity: number;
  notes?: string;
}

export interface UpdateSeatingData {
  table_name?: string;
  capacity?: number;
  notes?: string;
}

export interface AssignSeatingData {
  table_name: string;
  participant_ids: number[];
}

export interface InvitationData {
  event_name: string;
  participant_id: number;
  participant_name: string;
  rsvp_status: RSVPStatus;
  plus_ones: number;
  plus_one_names?: string[];
  dietary_restrictions?: string[];
  special_requests?: string;
  allow_plus_ones: boolean;
  max_plus_ones: number;
}

export interface CheckinData {
  qr_code: string;
}

export interface CheckinResponse {
  success: boolean;
  participant_name?: string;
  table_number?: string;
  message: string;
}

export interface ThankYouData {
  message: string;
  include_photos: boolean;
  photo_ids?: number[];
}

// ==================== API FUNCTIONS ====================

const BASE_PATH = "/event-management";

// 1. Create Event
export const createEvent = async (userId: number, data: CreateEventData): Promise<Event> => {
  return makeRequest(`${BASE_PATH}/${userId}/events`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 2. Get Event Details (with paginated participants)
export const getEventDetails = async (
  userId: number,
  eventId: number,
  participantsPage: number = 1,
  participantsLimit: number = 50
): Promise<EventDetail> => {
  return makeRequest(
    `${BASE_PATH}/${userId}/events/${eventId}?participants_page=${participantsPage}&participants_limit=${participantsLimit}`,
    {
      method: "GET",
    }
  );
};

// 3. Update Event
export const updateEvent = async (
  userId: number,
  eventId: number,
  data: Partial<CreateEventData>
): Promise<Event> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 4. Delete Event
export const deleteEvent = async (userId: number, eventId: number): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}`, {
    method: "DELETE",
  });
};

// 5. Get My Events (Paginated)
export const getMyEvents = async (
  userId: number,
  page: number = 1,
  limit: number = 20
): Promise<PaginatedResponse<Event>> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/my-events?page=${page}&limit=${limit}`, {
    method: "GET",
  });
};

// 6. Add Participants (Bulk)
export const addParticipants = async (
  userId: number,
  eventId: number,
  participants: AddParticipantData[]
): Promise<Participant[]> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/participants`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ participants }),
  });
};

// 7. Remove Participant
export const removeParticipant = async (
  userId: number,
  eventId: number,
  participantId: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/participants/${participantId}`, {
    method: "DELETE",
  });
};

// 7b. Update Participant Role
export const updateParticipantRole = async (
  userId: number,
  eventId: number,
  participantId: number,
  data: UpdateParticipantRoleData
): Promise<Participant> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/participants/${participantId}/role`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 8. Send Invitations
export const sendInvitations = async (
  userId: number,
  eventId: number
): Promise<{ success: boolean; sent_count: number }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/invite`, {
    method: "POST",
  });
};

// 9. View Invitation (Public - No Auth)
export const viewInvitation = async (inviteToken: string): Promise<InvitationData> => {
  return makePublicRequest(`${BASE_PATH}/events/rsvp/${inviteToken}`, {
    method: "GET",
  });
};

// 10. Submit RSVP (Public - No Auth)
export const submitRSVP = async (
  inviteToken: string,
  data: RSVPData
): Promise<{ success: boolean; event_id: number }> => {
  return makePublicRequest(`${BASE_PATH}/events/rsvp/${inviteToken}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 11. Update RSVP (Authenticated)
export const updateRSVP = async (
  userId: number,
  eventId: number,
  participantId: number,
  data: RSVPData
): Promise<{ success: boolean; event_id: number }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/rsvp/${participantId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 12. Add Task
export const addTask = async (userId: number, eventId: number, data: CreateTaskData): Promise<Task> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/tasks`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 13. Claim Task
export const claimTask = async (
  userId: number,
  eventId: number,
  taskId: number,
  quantity: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/tasks/${taskId}/claim`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ quantity }),
  });
};

// 13b. Update Task
export const updateTask = async (
  userId: number,
  eventId: number,
  taskId: number,
  data: UpdateTaskData
): Promise<Task> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/tasks/${taskId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 13c. Delete Task
export const deleteTask = async (
  userId: number,
  eventId: number,
  taskId: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/tasks/${taskId}`, {
    method: "DELETE",
  });
};

// 14. Add Rule
export const addRule = async (userId: number, eventId: number, data: CreateRuleData): Promise<Rule> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/rules`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 15. Update Rule
export const updateRule = async (
  userId: number,
  eventId: number,
  ruleId: number,
  data: UpdateRuleData
): Promise<Rule> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/rules/${ruleId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 15b. Delete Rule
export const deleteRule = async (
  userId: number,
  eventId: number,
  ruleId: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/rules/${ruleId}`, {
    method: "DELETE",
  });
};

// 16. Add Timeline Item
export const addTimelineItem = async (
  userId: number,
  eventId: number,
  data: CreateTimelineData
): Promise<TimelineItem> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/timeline`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 17. Update Timeline Item
export const updateTimelineItem = async (
  userId: number,
  eventId: number,
  timelineId: number,
  data: UpdateTimelineData
): Promise<TimelineItem> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/timeline/${timelineId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 17b. Delete Timeline Item
export const deleteTimelineItem = async (
  userId: number,
  eventId: number,
  timelineId: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/timeline/${timelineId}`, {
    method: "DELETE",
  });
};

// 18. Add Seating Table
export const addSeatingTable = async (
  userId: number,
  eventId: number,
  data: CreateSeatingData
): Promise<SeatingTable> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/seating`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 19. Assign Seating
export const assignSeating = async (
  userId: number,
  eventId: number,
  data: AssignSeatingData
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/seating/assign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 19b. Update Seating Table
export const updateSeatingTable = async (
  userId: number,
  eventId: number,
  tableId: number,
  data: UpdateSeatingData
): Promise<SeatingTable> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/seating/${tableId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 20. Delete Seating Table
export const deleteSeatingTable = async (
  userId: number,
  eventId: number,
  tableId: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/seating/${tableId}`, {
    method: "DELETE",
  });
};

// 20. Upload Media
export const uploadMedia = async (
  userId: number,
  eventId: number,
  file: File,
  mediaType: "photo" | "document" | "video",
  isPublic: boolean
): Promise<Media> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("media_type", mediaType);
  formData.append("is_public", isPublic.toString());

  return makeRequest(
    `${BASE_PATH}/${userId}/events/${eventId}/media`,
    {
      method: "POST",
      body: formData,
    }
  );
};

// 20b. Delete Media
export const deleteMedia = async (
  userId: number,
  eventId: number,
  mediaId: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/media/${mediaId}`, {
    method: "DELETE",
  });
};

// 21. QR Code Check-in
export const checkinWithQR = async (
  userId: number,
  eventId: number,
  data: CheckinData
): Promise<CheckinResponse> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/checkin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 21b. Validate QR Code
export const validateQRCode = async (userId: number, qrCode: string): Promise<{
  valid: boolean;
  event_id?: number;
  participant_id?: number;
  participant_name?: string;
  event_name?: string;
  already_checked_in: boolean;
  message: string;
}> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/validate-qr`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ qr_code: qrCode }),
  });
};

// 21c. Regenerate QR Code
export const regenerateQRCode = async (
  userId: number,
  eventId: number,
  participantId: number
): Promise<{
  success: boolean;
  qr_code_data: string;
  message: string;
}> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/regenerate-qr`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ participant_id: participantId }),
  });
};

// 22. Send Thank You Notes
export const sendThankYouNotes = async (
  userId: number,
  eventId: number,
  data: ThankYouData
): Promise<{ success: boolean; sent_count: number; message: string }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/thank-you`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 22.5. Add Custom Fun Fact
export const addFunFact = async (
  userId: number,
  eventId: number,
  data: FunFactCreate
): Promise<FunFact> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/fun-facts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });
};

// 23. Delete Fun Fact
export const deleteFunFact = async (
  userId: number,
  eventId: number,
  factId: number
): Promise<{ success: boolean }> => {
  return makeRequest(`${BASE_PATH}/${userId}/events/${eventId}/fun-facts/${factId}`, {
    method: "DELETE",
  });
};

